import { useRef } from 'react';
import { RescuePicker } from './RescuePicker';
import type { FrictionSubtype, InterruptionSource, PingType, RescueKind, RescuePrepChecklist } from '../types';
import type { RecentPingContext } from '../lib/rescuePreferences';

type PingRescueSheetProps = {
  pingId: string;
  pingType: PingType | null;
  topPickKind: RescueKind | null;
  subtype: FrictionSubtype | null;
  interruptionSource: InterruptionSource | null;
  scripts: Record<RescueKind, string>;
  defaultSound: boolean;
  favoriteRescues: RescueKind[];
  recentContext: RecentPingContext | null;
  showSavedHint: boolean;
  labels: {
    title: string;
    close: string;
    saved: string;
  };
  interruptionLabel: (source: InterruptionSource) => string;
  rescueKindLabel: (kind: RescueKind) => string;
  rescueGuide: (kind: RescueKind) => {
    summary: string;
    prompt: string | null;
    actionLabel: string;
    breathing: {
      intro: string;
      inhale: string;
      hold: string;
      exhale: string;
    };
  };
  prepItemLabel: (key: 'phoneSilenced' | 'tabsClosed' | 'docOpen' | 'timerVisible') => string;
  subtypeLabel: (subtype: FrictionSubtype) => string;
  rescuePickerLabels: {
    whatsHappening: string;
    pulledAway: string;
    none: string;
    chooseRescue: string;
    contextStep: string;
    contextHint: string;
    rescueStep: string;
    rescueHint: string;
    supportStep: string;
    supportHint: string;
    topPick: string;
    favorites: string;
    recentContextHint: string;
    applyLastContext: string;
    start: string;
    prepChecklist: string;
    resetTip: string;
    optionalDetails: string;
    smallestStepPlaceholder: string;
    start10mWithNote: string;
    sentencePlaceholder: string;
    saveSentence: string;
    focusSound: string;
    editRescueText: string;
    saveScript: string;
    savedSuffix: string;
    favoriteOn: string;
    favoriteOff: string;
  };
  onClose: () => void;
  onSaveScript: (kind: RescueKind, script: string) => void;
  onToggleFavorite: (kind: RescueKind) => void;
  onSetSubtype: (subtype: FrictionSubtype | null) => void;
  onSetInterruption: (source: InterruptionSource | null) => void;
  onApplyRecentContext: (context: RecentPingContext) => void;
  onStart: (kind: RescueKind, pingId: string, note: string | null, sound: boolean, prep: RescuePrepChecklist) => void;
};

export function PingRescueSheet({
  pingId,
  pingType,
  topPickKind,
  subtype,
  interruptionSource,
  scripts,
  defaultSound,
  favoriteRescues,
  recentContext,
  showSavedHint,
  labels,
  interruptionLabel,
  rescueKindLabel,
  rescueGuide,
  prepItemLabel,
  subtypeLabel,
  rescuePickerLabels,
  onClose,
  onSaveScript,
  onToggleFavorite,
  onSetSubtype,
  onSetInterruption,
  onApplyRecentContext,
  onStart
}: PingRescueSheetProps) {
  const touchStartY = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartScrollTop = useRef(0);

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div
        className="sheet"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0]?.clientX ?? null;
          touchStartY.current = e.touches[0]?.clientY ?? null;
          touchStartScrollTop.current = e.currentTarget.scrollTop;
        }}
        onTouchEnd={(e) => {
          if (touchStartY.current === null) return;
          const endX = e.changedTouches[0]?.clientX ?? touchStartX.current ?? 0;
          const end = e.changedTouches[0]?.clientY ?? touchStartY.current;
          const dy = end - touchStartY.current;
          const dx = endX - (touchStartX.current ?? endX);
          const mostlyVertical = Math.abs(dy) > Math.abs(dx) * 1.2;
          const startedAtTop = touchStartScrollTop.current <= 0;
          if (startedAtTop && mostlyVertical && dy > 140) onClose();
        }}
      >
        <div className="sheet-header">
          <h3>{labels.title}</h3>
          <button onClick={onClose}>{labels.close}</button>
        </div>
        <div className="sheet-intro">
          <div className="sheet-kicker">Recovery Note</div>
          {showSavedHint && <div className="small sheet-saved-note">{labels.saved}</div>}
        </div>
        <RescuePicker
          scripts={scripts}
          defaultSound={defaultSound}
          favoriteRescues={favoriteRescues}
          onToggleFavorite={onToggleFavorite}
          onSaveScript={onSaveScript}
          pingType={pingType}
          subtype={subtype}
          interruptionSource={interruptionSource}
          interruptionLabel={interruptionLabel}
          rescueKindLabel={rescueKindLabel}
          rescueGuide={rescueGuide}
          subtypeLabel={subtypeLabel}
          prepItemLabel={prepItemLabel}
          labels={rescuePickerLabels}
          topPickKind={topPickKind}
          recentContext={recentContext}
          onSetSubtype={onSetSubtype}
          onSetInterruption={onSetInterruption}
          onApplyRecentContext={onApplyRecentContext}
          onStart={(kind, note, sound, prep) => onStart(kind, pingId, note, sound, prep)}
        />
      </div>
    </div>
  );
}
